import { SHOT_OUTCOME_LABELS } from '@golf/common';
import type { ShotOutcome } from '@golf/common';
import { useId } from 'react';

/** Les six façons de manquer une balle, dans l'ordre où elles se placent
 *  autour du radar.
 *
 *  L'ordre n'est pas alphabétique, il est GÉOMÉTRIQUE : les deux fautes de
 *  direction se font face (slice à droite, hook à gauche), et les deux fautes
 *  de contact au sol aussi (top en haut, gratte en bas). Une forme lue sur ce
 *  radar dit donc quelque chose du geste — penchée à droite, on ouvre la face ;
 *  étirée verticalement, on ne trouve pas le bas de balle. Rangées autrement,
 *  les mêmes six chiffres ne dessineraient qu'un polygone quelconque. */
const AXES: Exclude<ShotOutcome, 'center'>[] = ['top', 'slice', 'shank', 'fat', 'hook', 'miss'];

export interface DefautsClub {
    slice: number;
    hook: number;
    top: number;
    fat: number;
    shank: number;
    miss: number;
}

/* Le radar des défauts d'un club.
 *
 * ── Pourquoi un radar, ici et pas ailleurs ──────────────────────────────────
 *
 * Partout ailleurs dans l'application, les comparaisons se font en barres ou en
 * colonnes : on y compare des grandeurs, et une longueur se compare mieux
 * qu'une aire. Ici la question est autre — non pas « combien de slices » mais
 * « de quoi ce club a-t-il l'air quand il rate ». C'est une FORME qu'on
 * cherche, et le radar est la seule figure qui en donne une : deux clubs aux
 * mêmes totaux mais aux défauts opposés dessinent deux silhouettes qu'on
 * distingue sans lire un chiffre.
 *
 * Il reste réservé à cet usage. Un radar pour comparer des taux serait le
 * mauvais outil, joliment employé.
 *
 * ── L'échelle ───────────────────────────────────────────────────────────────
 *
 * Elle part du plus haut défaut du club, jamais d'un maximum fixe : sur un club
 * bien maîtrisé, trois fautes réparties dessineraient sinon un point au centre.
 * Le radar dit la RÉPARTITION des ratés, pas leur nombre — celui-ci est écrit
 * en toutes lettres sous la figure, où il ne peut pas tromper.
 */
export function RadarDefauts({ defauts, taille = 240 }: {
    defauts: DefautsClub;
    taille?: number;
}) {
    const id = useId();
    const valeurs = AXES.map((a) => defauts[a]);
    const total = valeurs.reduce((n, v) => n + v, 0);
    const max = Math.max(1, ...valeurs);
    const nommes = AXES.filter((a) => defauts[a] > 0);

    /* En dessous de TROIS défauts distincts, le radar ment par sa forme.
     *
     * Un polygone à deux sommets n'est pas un polygone : c'est un segment, et
     * il se lit comme un trait tiré en travers de la toile plutôt que comme
     * une silhouette. À un seul sommet, c'est un point. La figure ne dit alors
     * plus rien de ce qu'elle est censée montrer — la répartition — et elle a
     * l'air cassée, ce qui est pire que de ne rien montrer.
     *
     * Deux fautes se lisent très bien en toutes lettres. On garde donc le
     * radar pour ce qu'il sait faire, et on écrit le reste. */
    if (nommes.length < 3) {
        return (
            <ul className="flex flex-wrap justify-center gap-1.5">
                {nommes.map((a) => (
                    <li key={a} className="rounded-field px-2 py-1 text-xs font-bold"
                        style={{ backgroundColor: 'var(--brique)', color: 'var(--brique-encre)' }}>
                        {SHOT_OUTCOME_LABELS[a]}
                        <span className="font-score ml-1.5 text-sm">{defauts[a]}</span>
                    </li>
                ))}
            </ul>
        );
    }

    const c = taille / 2;
    // De la place pour les étiquettes autour de la toile.
    const rayon = c - 34;

    /** Le point d'un axe, à une part donnée du rayon. */
    const point = (i: number, part: number): [number, number] => {
        // On démarre en haut et on tourne dans le sens horaire : c'est le sens
        // de lecture d'un cadran, et « top » se retrouve bien en haut.
        const angle = (Math.PI * 2 * i) / AXES.length - Math.PI / 2;
        return [c + Math.cos(angle) * rayon * part, c + Math.sin(angle) * rayon * part];
    };

    const polygone = (part: (i: number) => number): string =>
        AXES.map((_, i) => point(i, part(i)).join(',')).join(' ');

    return (
        <figure className="m-0">
            <svg viewBox={`0 0 ${taille} ${taille}`} className="mx-auto block h-auto w-full"
                style={{ maxWidth: taille }} role="img"
                aria-labelledby={`${id}-titre`}>
                <title id={`${id}-titre`}>
                    {total === 0
                        ? 'Aucune balle manquée avec ce club'
                        : `Répartition des ${total} balles manquées : ${AXES
                            .filter((a) => defauts[a] > 0)
                            .map((a) => `${SHOT_OUTCOME_LABELS[a]} ${defauts[a]}`)
                            .join(', ')}`}
                </title>

                {/* La toile : quatre anneaux, pas dix. Ils servent à situer un
                    sommet à peu près, pas à le mesurer — le chiffre exact est
                    écrit au bout de son axe. */}
                {[0.25, 0.5, 0.75, 1].map((r) => (
                    <polygon key={r} points={polygone(() => r)}
                        fill="none" stroke="var(--tuile-encre)" strokeOpacity={r === 1 ? 0.35 : 0.15}
                        strokeWidth={1} />
                ))}
                {AXES.map((a, i) => {
                    const [x, y] = point(i, 1);
                    return <line key={a} x1={c} y1={c} x2={x} y2={y}
                        stroke="var(--tuile-encre)" strokeOpacity={0.15} strokeWidth={1} />;
                })}

                {/* La surface. En BRIQUE : ce sont des balles manquées, et la
                    brique dit le coup raté partout ailleurs dans
                    l'application. Un vert ici féliciterait le défaut. */}
                {total > 0 && (
                    <polygon points={polygone((i) => valeurs[i]! / max)}
                        fill="var(--brique)" fillOpacity={0.55}
                        stroke="var(--brique-bord)" strokeWidth={2} strokeLinejoin="round" />
                )}
                {total > 0 && AXES.map((a, i) => {
                    if (defauts[a] === 0) return null;
                    const [x, y] = point(i, defauts[a] / max);
                    return <circle key={a} cx={x} cy={y} r={3} fill="var(--brique-bord)" />;
                })}

                {/* Les étiquettes, avec leur compte. Un axe à zéro reste
                    nommé mais s'efface : la figure garde ses six directions —
                    sans quoi elle changerait de forme d'un club à l'autre pour
                    une raison qui n'est pas le geste. */}
                {AXES.map((a, i) => {
                    const [x, y] = point(i, 1.22);
                    const vide = defauts[a] === 0;
                    return (
                        <text key={a} x={x} y={y} textAnchor="middle" dominantBaseline="middle"
                            style={{
                                fill: vide ? 'var(--tuile-encre)' : 'var(--brique-encre)',
                                fillOpacity: vide ? 0.4 : 1,
                                fontSize: 10,
                                fontWeight: 700,
                            }}>
                            {SHOT_OUTCOME_LABELS[a]}
                            {!vide && <tspan dx={3} style={{ fontSize: 11 }}>{defauts[a]}</tspan>}
                        </text>
                    );
                })}
            </svg>
        </figure>
    );
}
